import { flexRenderComponent } from '@tanstack/angular-table';
import { BooleanCell } from '../cells/boolean-cell.component';
import type { CustomFieldValueType, DataTableColumnDef } from '../types';
import {
  CUSTOM_FIELD_COLUMN_PREFIX,
  pickCustomFieldValue,
  type CustomFieldValueRow,
} from './custom-field-column';

/**
 * The minimal shape a consumer needs to produce a synthetic custom-field
 * column — the consumer-shaped projection of a `custom_field_definition`
 * (design.md D1's DTO idea, applied at the frontend fusion point). Entered
 * via `input()`/a port at the call site — this module (`@aurora`) imports
 * nothing from `@app` (HR-PORTS-001).
 */
export interface CustomFieldDefinitionInput {
  readonly definitionId: string;
  /** The `custom_field_group` this definition belongs to — the scoping key. */
  readonly groupId: string;
  readonly key: string;
  readonly type: CustomFieldValueType;
  readonly label: string | (() => string);
  readonly searchable: boolean;
  readonly filterable: boolean;
  /**
   * Select/multiselect general search AND per-column filter — client-side
   * term→optionId(s) resolver against the already-loaded option catalog
   * (active locale, see `custom-field-column.ts`'s `CustomFieldSearchColumn`
   * and `resolveCustomFieldFilter`). Omit for text/number/date/boolean.
   */
  readonly resolveOptionIds?: (term: string) => string[];
  /**
   * Select/multiselect cell rendering ONLY — optionId→display label
   * (active locale) resolver against the already-loaded option catalog.
   * Omit for text/number/date/boolean.
   */
  readonly resolveOptionLabel?: (optionId: string) => string;
}

const GENERAL_SEARCH_ELIGIBLE_TYPES: ReadonlySet<CustomFieldValueType> = new Set([
  'text',
  'select',
  'multiselect',
]);

/**
 * Runtime fusion of the statically generated columns with dynamic
 * custom-field columns (design.md D4, spec `custom-field-search`).
 *
 * **Scoping gate (permission-aware, not per-field ACL).** A definition
 * produces a synthetic column ONLY when its `groupId` is present in
 * `associatedGroupIds` — the pre-scoped set of groups whose `entityTypes`
 * array-contains the entity actually being queried
 * (remodel-custom-field-group-association, design.md D2 — replaces the
 * retired `custom_field_group_entity` pivot lookup). A
 * definition whose group is NOT in that set is silently omitted: it never
 * becomes a column, so it never reaches `buildSearchWhere` / `buildFilterWhere`
 * and never reaches `where`. There is no per-definition permission check —
 * values inherit the parent's permissions (no per-field ACL exists); the
 * caller is responsible for resolving `associatedGroupIds` only after
 * confirming the account can query the parent list.
 *
 * **Never sortable.** Every synthetic column is `sortable: false` — v1 does
 * not support ordering by `customFieldValues` (a to-many relation; the DSL
 * top-level `order` dot-path only accepts to-one relations).
 *
 * **General-search type guard.** `searchable` is forced to `false` for
 * `number`/`date`/`boolean` definitions even when the definition itself
 * opts in — an `iLike` over those columns is a type mismatch (design.md D4).
 * Only `text`/`select`/`multiselect` can participate in the general
 * (free-text) search box.
 *
 * @param staticColumns - The generated columns for the entity, unchanged.
 * @param definitions - Custom-field definitions already resolved for this
 *   consumer (e.g. via the admin GraphQL API), REGARDLESS of group
 *   association — the function itself applies the scoping gate.
 * @param associatedGroupIds - The `groupId`s whose `entityTypes` contains the
 *   queried entity's `entityType` (array-contains resolution).
 */
export function mergeCustomFieldColumns<T>(
  staticColumns: DataTableColumnDef<T>[],
  definitions: readonly CustomFieldDefinitionInput[],
  associatedGroupIds: ReadonlySet<string> | readonly string[],
): DataTableColumnDef<T>[] {
  const scope =
    associatedGroupIds instanceof Set ? associatedGroupIds : new Set(associatedGroupIds);

  const syntheticColumns = definitions
    .filter((definition) => scope.has(definition.groupId))
    // An ENCRYPTED definition gets NO column at all — no read ever returns the value, so a
    // column would always render an empty cell. Filtered out here, before ANY column-shape
    // decision, so it never reaches buildSearchWhere/buildFilterWhere and never reaches where.
    .filter((definition) => definition.type !== 'encrypted')
    .map((definition) => toSyntheticColumn<T>(definition));

  return [...staticColumns, ...syntheticColumns];
}

function toSyntheticColumn<T>(definition: CustomFieldDefinitionInput): DataTableColumnDef<T> {
  const generalSearchEligible = GENERAL_SEARCH_ELIGIBLE_TYPES.has(definition.type);

  return {
    id: `${CUSTOM_FIELD_COLUMN_PREFIX}${definition.definitionId}`,
    // No static accessor exists for a synthetic column — its value lives in
    // the row's `customFieldValues` relation, matched by definitionId, then
    // narrowed to the ONE typed field the definition's `type` populates.
    accessorFn: (row: T) =>
      pickCustomFieldValue(findCustomFieldValueRow(row, definition.definitionId), definition.type),
    label: definition.label,
    sortable: false,
    filterable: definition.filterable,
    searchable: definition.searchable && generalSearchEligible,
    // Every synthetic column carries the association so `getRelationsNode`
    // eager-loads `customFieldValues` on every paginate query that includes
    // it — without this, the accessor above always resolves to `undefined`.
    relation: { association: 'customFieldValues' },
    customField: { definitionId: definition.definitionId, type: definition.type },
    cell:
      definition.type === 'boolean'
        ? () => flexRenderComponent(BooleanCell, { inputs: {} })
        : (info) => formatCustomFieldCellValue(info.getValue(), definition),
  };
}

function findCustomFieldValueRow<T>(row: T, definitionId: string): CustomFieldValueRow | undefined {
  return (
    row as unknown as { customFieldValues?: ReadonlyArray<CustomFieldValueRow> }
  ).customFieldValues?.find((v) => v.definitionId === definitionId);
}

function formatCustomFieldCellValue(
  value: unknown,
  definition: CustomFieldDefinitionInput,
): string {
  switch (definition.type) {
    case 'select':
      return typeof value === 'string' ? (definition.resolveOptionLabel?.(value) ?? value) : '';
    case 'multiselect':
      return Array.isArray(value)
        ? value
            .map((optionId: string) => definition.resolveOptionLabel?.(optionId) ?? optionId)
            .join(', ')
        : '';
    case 'number':
      return typeof value === 'number' ? String(value) : '';
    default:
      return typeof value === 'string' ? value : '';
  }
}
